import { useCallback, useEffect, useMemo, useRef, useState, type ClipboardEvent as ReactClipboardEvent } from 'react';
import { useBlocker } from '@tanstack/react-router';
import {
  AlertTriangle,
  AppWindow,
  Check,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Copy,
  Download,
  Eye,
  EyeOff,
  Plus,
  ShieldCheck,
  Terminal,
  Trash2,
} from 'lucide-react';
import { cn } from '@siliconx/shared/ui/utils';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { LoadingShell } from '@siliconx/shared/ui/state';
import { DisclosurePanel } from '@siliconx/shared/ui/DisclosurePanel';
import { useAuth } from '../lib/authContext';
import { api, throwIfNotOk } from '../lib/api';
import { withMinimumDuration } from '../lib/minDuration';
import {
  buildProbeCommand,
  buildProbeCommandDisplay,
  buildProbeLauncher,
  detectProbeOs,
  PROBE_OS_LABELS,
  slugifyAccountName,
  type DetectedOs,
  type ProbeOs,
} from '../lib/probeCommand';
import { ProbeIngestFeed, type ProbeIngestRow } from './ProbeIngestFeed';

/**
 * SIL-1213/SIL-1812: "How this works" step strip - three short steps, each
 * labelled where it happens. SIL-1812 cut the old 5/6-step variants (and the
 * profile step, which no longer belongs to this flow) down to the shape of the
 * whole task: build here, run it on the machine you want to add, review what
 * we found.
 */
export type HowStepId = 'build' | 'run' | 'review';

export interface HowStep {
  id: HowStepId;
  n: number;
  label: string;
  where: 'browser' | 'machine';
  /** Overrides the where-label sub-text. */
  description?: string;
}

export const HOW_THIS_WORKS_STEPS: readonly HowStep[] = [
  { id: 'build', n: 1, label: 'Build your probe here', where: 'browser' },
  { id: 'run', n: 2, label: 'Run it on the machine you want to add', where: 'machine' },
  { id: 'review', n: 3, label: 'Review the hardware we found', where: 'browser' },
];

const HOW_STEP_WHERE_LABEL: Record<HowStep['where'], string> = {
  browser: 'in your browser',
  machine: 'on the machine you’re adding',
};

/**
 * SIL-1213: derives the current step from state this component already
 * tracks. Exported (pure function, no JSX) so the sequencing itself is
 * unit-testable without rendering the panel. `hasReadings` is the one signal
 * this component does not otherwise keep - see `handleRowsChange` below,
 * which exists to produce it without touching `ProbeIngestFeed`.
 */
export function currentHowStep(state: {
  hasSession: boolean;
  mintedToken: string | null;
  secretHandedOff: boolean;
  hasReadings: boolean;
}): HowStepId {
  if (!state.hasSession) return 'build';
  // A just-minted secret that hasn't been copied yet - the one narrow window
  // where "copy" is genuinely next. A resumed session (mintedTokenId but no
  // mintedToken - the secret never survives a reload) has nothing left to
  // copy, so it skips straight past this step.
  // (copying the command is the first half of "run it on the machine").
  if (state.mintedToken && !state.secretHandedOff) return 'run';
  if (!state.hasReadings) return 'run';
  return 'review';
}

/**
 * SIL-1213: the always-visible orientation strip - current step highlighted,
 * every step labelled with where it happens. Rendered before any
 * instruction, per the ticket's root-cause fix (this replaces ~200 words of
 * unstructured prose that used to come first with no stated procedure).
 */
function HowThisWorksStrip({ current }: { current: HowStepId }) {
  return (
    <div className="rounded-md border border-border bg-muted/20 p-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">How this works</p>
      <ol className="mt-2 grid grid-cols-1 gap-2 lg:grid-cols-3">
        {HOW_THIS_WORKS_STEPS.map((step) => {
          const isCurrent = step.id === current;
          return (
            <li
              key={step.id}
              aria-current={isCurrent ? 'step' : undefined}
              className={cn(
                'rounded-md border px-2 py-1.5 text-xs',
                isCurrent
                  ? 'border-primary/50 bg-primary/10 text-foreground'
                  : 'border-transparent text-muted-foreground',
              )}
            >
              <span className="font-semibold">{step.label}</span>
              <span className="block text-xs text-muted-foreground">
                {step.description ?? HOW_STEP_WHERE_LABEL[step.where]}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * SIL-1213: a macOS browser gets an explicit not-supported notice instead of
 * a Linux command that cannot work (the probe script exits on a non-Linux
 * host). The Linux/Windows tabs stay selectable underneath - a Mac user may
 * legitimately be preparing a command to run on a separate Linux or Windows
 * box, so this does not block the build, and neither tab is auto-selected as
 * if it will run on the Mac itself.
 */
function MacNotSupportedNotice() {
  // SIL-1812: informational, not a warning - a Mac user is the normal case
  // (build here, run on the machine to add), so no warning colour or icon.
  return (
    <p className="rounded-md border border-border bg-muted/20 p-3 text-xs text-foreground">
      You are on a Mac. The probe runs on the Linux or Windows machine you want to add, not on this Mac.
      Build it here, then copy the command to that machine.
    </p>
  );
}

/** SIL-1197: the Reveal toggle never leaves the token showing indefinitely -
 * it masks itself again this long after the most recent reveal, matching the
 * "never saved across reloads" requirement (component state only, no
 * persistence, and this timer is the belt to that braces). */
const REVEAL_AUTO_MASK_MS = 30_000;
/** SIL-1197: how long the Copy CTA shows "Full command copied." before
 * reverting to its normal look/label - Copy stays the primary CTA the whole
 * time, there is no separate demoted/done state. */
const COPY_CONFIRM_MS = 2_000;

/** SIL-1194: the build spinner never resolves faster than this, even when
 * the API responds instantly - see `withMinimumDuration`. Exported so tests
 * can assert against the same values instead of restating them. */
export const MIN_BUILD_MS = 1200;
/** ms of continuous `minting` before the label switches to "Still building…". */
export const STILL_BUILDING_MS = 5_000;
/** ms of continuous `minting` before Cancel/Retry appear. */
export const STUCK_MS = 15_000;

/** Approved copy (QA UX-05) - shown verbatim on a failed build. */
export const BUILD_FAILURE_COPY = "We couldn't build your probe. Try again — nothing was charged or changed.";

/** Distinguishes a 409 (another change racing this one) from every other
 * failure, so `mintToken`'s catch block can pick the right message without
 * inspecting error internals. */
class MintConflictError extends Error {}

/**
 * Per-platform requirements and run instructions, sourced from the actual
 * probe scripts' own header comments (the source of truth):
 *   apps/verify-harness/runner/inventory-probe.v5.sh   (Linux)
 *   apps/verify-harness/runner/inventory-probe.v8.ps1  (Windows)
 * Keep this in sync when either script's requirements change - it is
 * documentation of the real script, not a generic checklist.
 */
const PROBE_REQUIREMENTS: Record<ProbeOs, { requirements: string[] }> = {
  linux: {
    requirements: [
      'A physical Linux machine - not a VM and not WSL. The probe checks this itself (systemd-detect-virt, falling back to DMI) and refuses to run otherwise.',
      'python3 on PATH - required; the probe cannot run at all without it.',
      "The GPU vendor's driver already installed, so its CLI can report a device serial (nvidia-smi, amd-smi/rocm-smi, hl-smi, or xpu-smi, whichever applies).",
      'curl on PATH - only needed to send results (the command below always uses it); no network calls happen otherwise.',
      'lspci (the pciutils package) - optional, only used to report PCI addresses.',
      'No root or elevated privileges of any kind are needed.',
    ],
  },
  windows: {
    requirements: [
      'Windows Server 2012 R2 or later (Windows PowerShell 4.0+ and .NET Framework 4.5+; TLS 1.2 is enabled automatically), or PowerShell 7+. Windows Server 2012 (PowerShell 3.0) is not supported.',
      'The physical Windows host - not a VM. The probe checks this itself and refuses to run otherwise.',
      'No Administrator elevation needed - everything the probe reads is available to a standard user.',
      "The GPU vendor's driver already installed, so its CLI can report a device serial (nvidia-smi.exe, amd-smi.exe/rocm-smi.exe, hl-smi.exe, or xpu-smi.exe, whichever applies) - falls back to Windows' own device inventory when absent.",
      "Default execution policy (Restricted) is fine - the command below scopes '-ExecutionPolicy Bypass' to the one child process running the script; it changes nothing else on the machine and does not require Set-ExecutionPolicy.",
    ],
  },
};

/**
 * SIL-1213: "Paste the command below..." only ever makes sense once a
 * command actually exists - moved out of `PROBE_REQUIREMENTS` (which used to
 * render pre-build too, when there was nothing to paste yet) and rendered
 * only next to the built command itself, in the post-build command block.
 */
const RUN_INSTRUCTIONS: Record<ProbeOs, string> = {
  linux: 'Paste the command below into a terminal on the target Linux machine and run it as your normal user (no sudo).',
  windows: 'Paste the command below into a PowerShell prompt on the target Windows machine - no elevation needed.',
};

/** SIL-1226: icon for each OS in the segmented selector - lucide has no
 * literal Linux/Windows brand marks, so these are the closest generic
 * stand-ins (a terminal for Linux, a windowed app for Windows). */
const PROBE_OS_ICONS: Record<ProbeOs, typeof Terminal> = {
  linux: Terminal,
  windows: AppWindow,
};

/** Mirrors GET /api/client/inventory/tokens (never the secret). */
interface InventoryTokenRow {
  id: string;
  label: string | null;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
  lastUsedAt: string | null;
}

export type ProbeNotify = (message: string, type: 'success' | 'error') => void;

/** SIL-1127: downloads the .cmd launcher client-side via a Blob - no API call, no server involvement.
 * SIL-1197: named for the account (`trueread-probe-<account-slug>.cmd`) rather than a fixed filename,
 * so a launcher saved to disk (Downloads folder, a shared runbook, a support ticket) is identifiable
 * on its own - see `slugifyAccountName`'s docstring. */
function downloadLauncher(launcher: string, accountSlug: string) {
  const blob = new Blob([launcher], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `trueread-probe-${accountSlug}.cmd`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface ProbeCommandPanelProps {
  /**
   * Where success and error messages go: /customs renders its own Toast, Add
   * Asset uses sonner. Must be referentially stable (module-level or
   * useCallback): it is a dependency of the token load effect.
   */
  notify: ProbeNotify;
  /** The probe table is a Customs concern; Add Asset passes false. */
  showTokenManagement?: boolean;
  /** Fires once, right after a probe is successfully built. SIL-1183's wizard stepper drives progress off this. */
  onProbeBuilt?: () => void;
  /** Forwarded to `ProbeIngestFeed` - fires with the full list of matched readings whenever the ingest feed changes. */
  onMatchesChange?: (matches: ProbeIngestRow[]) => void;
  /** SIL-1195: forwarded to `ProbeIngestFeed` - fires with every row (any status) whenever the ingest feed changes. */
  onRowsChange?: (rows: ProbeIngestRow[]) => void;
  /** SIL-1194 timing knobs - production relies on the `MIN_BUILD_MS`/
   * `STILL_BUILDING_MS`/`STUCK_MS` defaults; tests override them to avoid
   * real waiting. */
  minBuildMs?: number;
  stillBuildingMs?: number;
  stuckMs?: number;
  /** SIL-1208: an inventory token id to resume from - e.g. a `?probeTokenId=`
   * search param a caller restores on mount, so a reload lands back on the
   * same session's readings feed. Read once, on mount, via `useState`'s lazy
   * initializer - later prop changes are not tracked, matching how a route's
   * own search param is only ever meant to seed the first render here. Never
   * the secret itself: only the opaque token row id, which is safe to put in
   * a URL and to resend to the org-scoped ingests endpoint. */
  resumeTokenId?: string | null;
  /** SIL-1208: fires whenever the active/resumable token id changes (after a
   * successful mint, or once on mount if resuming), so the caller can mirror
   * it into the URL for reload/back-button resume. */
  onTokenIdChange?: (tokenId: string | null) => void;
  /** SIL-1211: once a session exists (`hasSession`), suppress the build
   * explainer (`introProse`) and the collapsed requirements disclosure
   * (`requirementsSummary`) - used by the Add Asset wizard's "Run & capture"
   * step, which must render run content only (the command/launcher, the
   * readings feed, the finish action) with no build-step context mixed in.
   * Customs (`showTokenManagement=true`, no step model) leaves this false -
   * unaffected. */
  hideBuildContextOnceMinted?: boolean;
}

/**
 * SIL-1208: shown while `useBlocker`'s resolver is `'blocked'` - i.e. the
 * user is trying to leave (in-app navigation) with a just-minted, not-yet-
 * copied command still on screen. The same `enableBeforeUnload` option also
 * triggers the browser's own native reload/close-tab prompt independently of
 * this dialog (that one's wording is the browser's, not ours, and cannot be
 * customized - this dialog is the in-app equivalent).
 */
function LeaveGuardDialog({
  open,
  onStay,
  onLeave,
}: {
  open: boolean;
  onStay: () => void;
  onLeave: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-sm rounded-xl bg-card p-6 shadow-xl">
        <h2 className="mb-2 text-base font-semibold text-foreground">Leave without copying the command?</h2>
        <p className="mb-2 text-sm text-muted-foreground">
          This command is visible only in this browser session. If you leave now without copying it, it
          can&rsquo;t be shown again - you&rsquo;d need to build a new probe.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onStay}
            className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
          >
            Stay on this page
          </button>
          <Button onClick={onLeave}>Leave anyway</Button>
        </div>
      </div>
    </div>
  );
}

/**
 * SIL-1197 (QA UX-05 part 2 / UX-03): the post-build command block. Masked by
 * default (see `buildProbeCommandDisplay`); `command` — never `maskedCommand`
 * — is the only string that ever leaves the page, and it lives only in this
 * closure/a ref, never a DOM attribute. Three independent ways to get the
 * full, runnable command:
 *
 *  1. Click Copy: `navigator.clipboard.writeText`, falling back to a hidden
 *     textarea + `document.execCommand('copy')` when the Clipboard API is
 *     unavailable or denied. Copy is the panel's one primary-CTA accent
 *     element and stays that way after a click — its label swaps to "Full
 *     command copied." for ~2s, then reverts. No demoted/done state, and a
 *     second click behaves exactly like the first.
 *  2. Select the on-screen (masked) text and press Cmd/Ctrl-C: the `copy`
 *     event handler on the wrapper overrides whatever was selected with the
 *     real command, so the clipboard never ends up holding the mask.
 *  3. If both the Clipboard API and `execCommand` fail, Reveal fires
 *     automatically and the error names it as the way out - the user is
 *     never left holding a command they cannot copy by any means.
 *
 * Reveal (independent of Copy) is for manual entry on another machine: off
 * by default, re-masks itself `REVEAL_AUTO_MASK_MS` after the most recent
 * reveal, and is never persisted (plain component state) — a reload always
 * lands back on masked.
 */
function CommandBlock({
  command,
  maskedCommand,
  notify,
  onAcquired,
}: {
  command: string;
  maskedCommand: string;
  notify: ProbeNotify;
  /** SIL-1208: fires the moment the user has gotten the real command off the
   * page by any path (Copy button, manual Ctrl/Cmd-C selection, or the
   * reveal-on-failure fallback's own eventual copy) - feeds the parent's
   * leave-guard, which only warns before the secret has been handed off. */
  onAcquired?: () => void;
}) {
  const [revealed, setRevealed] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');
  const [announcement, setAnnouncement] = useState('');
  // A ref (not just the `command` prop) so the async clipboard callbacks and
  // the native `copy` event listener always read the latest value without
  // becoming a stale closure - and so the real token is never round-tripped
  // through a DOM attribute to get there.
  const commandRef = useRef(command);
  commandRef.current = command;
  const revealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    },
    [],
  );

  function reveal() {
    setRevealed(true);
    setAnnouncement('Token revealed');
    if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
    revealTimerRef.current = setTimeout(() => {
      setRevealed(false);
      setAnnouncement('Token hidden');
    }, REVEAL_AUTO_MASK_MS);
  }

  function hide() {
    if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
    setRevealed(false);
    setAnnouncement('Token hidden');
  }

  function markCopied() {
    notify('Full command copied.', 'success');
    setCopyState('copied');
    onAcquired?.();
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopyState('idle'), COPY_CONFIRM_MS);
  }

  /** Last-resort fallback when the async Clipboard API is unavailable or denied. */
  function legacyCopy(text: string): boolean {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    document.body.removeChild(textarea);
    return ok;
  }

  async function handleCopyClick() {
    try {
      await navigator.clipboard.writeText(commandRef.current);
      markCopied();
      return;
    } catch {
      // Clipboard API missing/denied - fall through to the execCommand fallback.
    }
    if (legacyCopy(commandRef.current)) {
      markCopied();
      return;
    }
    // Both copy paths failed - the user must never be stuck with a command
    // they can't get off the page, so reveal it for manual selection.
    reveal();
    notify('Could not copy automatically - the full command above is now shown. Select and copy it by hand.', 'error');
  }

  /** AC: selecting the (possibly masked) text and pressing Cmd/Ctrl-C must
   * still put the FULL command on the clipboard, never the on-screen mask. */
  function handleCopyEvent(e: ReactClipboardEvent<HTMLDivElement>) {
    const selection = window.getSelection?.()?.toString() ?? '';
    if (!selection || !e.clipboardData) return;
    e.preventDefault();
    e.clipboardData.setData('text/plain', commandRef.current);
    markCopied();
  }

  return (
    <div className="space-y-2">
      <div
        onCopy={handleCopyEvent}
        className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 font-mono text-xs"
      >
        <span className="min-w-0 flex-1 break-all">{revealed ? command : maskedCommand}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => void handleCopyClick()} className="gap-1.5">
          {copyState === 'copied' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copyState === 'copied' ? 'Full command copied.' : 'Copy'}
        </Button>
        <button
          type="button"
          onClick={() => (revealed ? hide() : reveal())}
          aria-pressed={revealed}
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted"
        >
          {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {revealed ? 'Hide' : 'Reveal'}
        </button>
      </div>
      {!revealed && <p className="text-xs text-muted-foreground">Hidden here. Complete when you copy.</p>}
      {/* Polite live announcements: the Reveal state change (sr-only, redundant
          with the button's own label swap) and, permanently, that Copy always
          sends the complete command regardless of what the mask shows on
          screen - a screen-reader user reading the masked span alone has no
          other way to know the copied value isn't literally dots. */}
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
      <p className="sr-only">Copy always sends the complete command - the dots on screen are a display mask only.</p>
    </div>
  );
}

// One active token per org (SIL-695 follow-up), so generating a new one
// replaces whatever is currently live. Only shown when there IS an active
// token to replace; the very first mint needs no confirmation.
function MintConfirmDialog({
  open,
  activeToken,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  activeToken: InventoryTokenRow | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open || !activeToken) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-sm rounded-xl bg-card p-6 shadow-xl">
        <h2 className="mb-2 text-base font-semibold text-foreground">Build a new probe?</h2>
        <p className="mb-2 text-sm text-muted-foreground">
          This organization already has a live probe{activeToken.label ? ` (“${activeToken.label}”)` : ''}, last
          used{' '}
          {activeToken.lastUsedAt ? new Date(activeToken.lastUsedAt).toLocaleString() : 'never'}. Only one probe
          can be active at a time. Building a new probe stops this one from working. Any automation or script
          still carrying it will fail until it is updated with the new probe.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <Button onClick={onConfirm} disabled={busy}>
            {busy ? 'Building your probe…' : 'Build new probe'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * SIL-710 published a Windows probe beside the Linux one and this page never
 * surfaced it, so a customer on Windows had no way to discover it - the only
 * command here was `curl | sha256sum -c | sh`, which is not runnable on a
 * Windows host and quietly reads as "SiliconX does not support Windows".
 *
 * Both commands are checksum-gated against the pin in probe-pins.ts before
 * anything executes. The PowerShell comparison uses `-ne`, which is
 * case-insensitive on strings, so the lowercase pin matches `Get-FileHash`'s
 * uppercase output as written.
 *
 * TOKEN-GATED, not print-mode (this page, follow-up to SIL-695): a command is
 * only ever rendered once an inventory token has been minted, and the token
 * is baked into the one-liner via `buildProbeCommand` - there is no
 * `--print`/`-Print` command to copy anymore, because a copied print-mode
 * command made zero network calls and never actually sent anything to
 * SiliconX. See `lib/probeCommand.ts` for the exact invocations.
 *
 * The probe runs in a CHILD PowerShell with a process-scoped
 * `-ExecutionPolicy Bypass`, and that is load-bearing, not belt-and-braces.
 * The first version of this command used `Unblock-File` instead, on the theory
 * that the only obstacle was the mark-of-the-web `Invoke-WebRequest` attaches.
 * It is not: Windows PowerShell 5.1 - the version the probe targets and the one
 * a customer gets by default - ships with execution policy **Restricted**,
 * which refuses EVERY script whatever its origin or signature, so clearing the
 * mark changes nothing and the run dies with "running scripts is disabled on
 * this system" (reported from a real Windows host, 2026-08-21).
 *
 * Signing it would not help either: the probe is Sigstore-signed, not
 * Authenticode-signed, so there is no signature Windows itself will accept, and
 * Restricted blocks signed scripts anyway.
 *
 * `-ExecutionPolicy Bypass` on the child process is the narrow door, not the
 * wide one - it lasts exactly as long as that one process, changes nothing on
 * the machine, and applies only to bytes whose sha256 we just checked against
 * the pin. The wide door would be `Set-ExecutionPolicy` at machine or user
 * scope, which outlives the run and covers every future script. Note this
 * cannot beat a Group-Policy-set policy (MachinePolicy/UserPolicy scope
 * outranks the command line); a host locked down that way needs an
 * administrator, and no install command can route around it.
 */
export function ProbeCommandPanel({
  notify,
  showTokenManagement = true,
  onProbeBuilt,
  onMatchesChange,
  onRowsChange,
  minBuildMs = MIN_BUILD_MS,
  stillBuildingMs = STILL_BUILDING_MS,
  stuckMs = STUCK_MS,
  resumeTokenId = null,
  onTokenIdChange,
  hideBuildContextOnceMinted = false,
}: ProbeCommandPanelProps) {
  const { user, displayName } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [tokens, setTokens] = useState<InventoryTokenRow[]>([]);
  // Members never call the admin-only token list, so they start "loaded".
  const [tokensLoaded, setTokensLoaded] = useState(!isAdmin);
  // SIL-1208: the secret itself is NEVER recoverable after a reload - only
  // `mintedTokenId` seeds from `resumeTokenId`. A resumed session therefore
  // starts with a token id but no secret, which the render below treats as
  // its own "resumed, command unavailable" state rather than pretending
  // nothing was ever built.
  const [mintedToken, setMintedToken] = useState<string | null>(null);
  const [mintedTokenId, setMintedTokenId] = useState<string | null>(resumeTokenId);
  /** SIL-1208: true once the user has gotten the current secret off the page
   * (Copy, manual select-and-copy, or download the Windows launcher) - drives
   * the leave guard below. Reset to false by every fresh mint. */
  const [secretHandedOff, setSecretHandedOff] = useState(false);
  // SIL-1213: detected once, on mount - `detectedOs` may be 'macos', which is
  // not a valid `ProbeOs` (there is no macOS probe; see probeCommand.ts). The
  // notice below reads off this raw value; the tab selection below reads off
  // a ProbeOs-narrowed fallback so 'macos' never silently becomes 'linux'
  // again as an auto-selected, apparently-runnable default.
  const [detectedOs] = useState<DetectedOs | null>(() => detectProbeOs());
  // Auto-select the tab matching the OS running this browser when it's one
  // the probe actually supports; otherwise (macOS, or nothing detected) keep
  // the previous fixed default - never auto-select as if it will run here.
  const [probeOs, setProbeOs] = useState<ProbeOs>(() =>
    detectedOs === 'linux' || detectedOs === 'windows' ? detectedOs : 'linux',
  );
  /** SIL-1213: set by `handleRowsChange` (wraps the `onRowsChange` prop) -
   * the one signal `currentHowStep` needs that this component doesn't
   * otherwise keep. Never inspects `ProbeIngestFeed` internals directly. */
  const [hasReadings, setHasReadings] = useState(false);
  const [minting, setMinting] = useState(false);
  /** SIL-1194: drives the CTA's progressive copy/affordances while `minting`
   * is true - 'building' -> 'slow' (~5s, "Still building…") -> 'stuck'
   * (~15s, adds Cancel/Retry). Reset to 'idle' the moment minting stops. */
  const [buildStage, setBuildStage] = useState<'idle' | 'building' | 'slow' | 'stuck'>('idle');
  const [confirmMintOpen, setConfirmMintOpen] = useState(false);
  // Windows only (SIL-1127): the raw copy-paste command is secondary to the
  // one-click .cmd download, collapsed behind "Need more control?" by
  // default. Reset whenever the tab changes so switching away and back
  // always lands collapsed again.
  const [showRawCommand, setShowRawCommand] = useState(false);
  /** SIL-1197: post-build, the requirements list collapses to a one-line
   * "<OS> requirements ✓" summary behind this disclosure. Reset on OS switch,
   * same as `showRawCommand`, so changing tabs always lands collapsed again. */
  const [showRequirements, setShowRequirements] = useState(false);
  /** SIL-1197: focus target for "On a successful build, focus moves to the
   * 'Your probe is ready' heading." */
  const readyHeadingRef = useRef<HTMLHeadingElement>(null);
  /** SIL-1226: "Wrong OS? Change it" on the probe-ready card - re-shows the OS
   * selector inline so a user can swap the pasted command for the other OS
   * without rebuilding. Reset by `selectOs` once a choice is made, and by a
   * fresh mint (a new build starts collapsed like everything else here). */
  const [showOsSwitcher, setShowOsSwitcher] = useState(false);
  /** SIL-1812: when the OS was detected (linux/windows), the picker is a one-line "Built for X - Change"; Change reveals the buttons. */
  const [osChooserOpen, setOsChooserOpen] = useState(false);

  /** What the confirmation step warns about replacing. */
  const activeToken = useMemo(() => tokens.find((t) => !t.revokedAt) ?? null, [tokens]);

  /** SIL-1208: true from either the token id (resumed or just-minted) or the
   * secret itself - branching on this instead of `mintedTokenId` alone keeps
   * "a probe exists" correct even if a caller's mint response ever omits
   * `id` (the secret is always the ground truth for "something was built"). */
  const hasSession = !!mintedTokenId || !!mintedToken;

  /** `null` with no token, or (defensive) a token that fails buildProbeCommand's shape check. */
  const command = useMemo(() => buildProbeCommand(probeOs, mintedToken), [probeOs, mintedToken]);

  /** SIL-1189: screen-only - the token masked, everything else identical. Never passed to CommandBlock's Copy or the launcher download. */
  const maskedCommand = useMemo(() => buildProbeCommandDisplay(probeOs, mintedToken), [probeOs, mintedToken]);

  /** Windows-only (SIL-1127); `null` under the same conditions `command` is null. */
  const launcher = useMemo(
    () => (probeOs === 'windows' ? buildProbeLauncher(mintedToken) : null),
    [probeOs, mintedToken],
  );

  /** SIL-1197: names the downloaded Windows launcher for the account. */
  const accountSlug = useMemo(() => slugifyAccountName(displayName), [displayName]);

  /** SIL-1213: wraps the `onRowsChange` prop so this component also learns
   * "has at least one reading landed" for the "How this works" strip, without
   * reaching into `ProbeIngestFeed`'s own state. */
  const handleRowsChange = useCallback(
    (rows: ProbeIngestRow[]) => {
      setHasReadings(rows.length > 0);
      onRowsChange?.(rows);
    },
    [onRowsChange],
  );

  /** SIL-1213: drives the always-visible "How this works" strip. */
  const howStep = currentHowStep({ hasSession, mintedToken, secretHandedOff, hasReadings });

  /** SIL-1208: a resumed session (mintedTokenId from the URL, no secret) whose
   * token the admin-only list shows as gone or revoked - by this admin
   * rebuilding elsewhere, or another admin doing so. Non-admins never load
   * `tokens` at all (see `loadTokens`), so this always reads false for them;
   * they still see the read-only readings feed, just with no revoked notice. */
  const resumedTokenRevoked =
    isAdmin && tokensLoaded && !!mintedTokenId && !mintedToken &&
    !tokens.some((t) => t.id === mintedTokenId && !t.revokedAt);

  // SIL-1208: mirror the active/resumable token id out to the caller (e.g. a
  // `?probeTokenId=` URL search param) so a reload lands back on this same
  // session's readings feed. Fires on mount too (harmless no-op resync when
  // resuming from that same id) and again on every fresh mint.
  useEffect(() => {
    onTokenIdChange?.(mintedTokenId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onTokenIdChange is a fresh closure every render; only mintedTokenId should retrigger this
  }, [mintedTokenId]);

  // SIL-1208: a resumed session lost its wizard-progress memory along with
  // everything else in React state - the build itself still happened (the
  // token id proves it), so the caller's "probe built" bookkeeping should
  // reflect that too. Fires once, only when mounting already resumed.
  const resumeNotifiedRef = useRef(false);
  useEffect(() => {
    if (resumeNotifiedRef.current || !resumeTokenId) return;
    resumeNotifiedRef.current = true;
    onProbeBuilt?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once on mount only
  }, []);

  /**
   * SIL-1208: leave guard. Blocks in-app navigation away (with the confirm
   * dialog below) AND the browser's own reload/close-tab prompt
   * (`enableBeforeUnload`) whenever a just-minted secret hasn't been handed
   * off yet - it is the one and only chance to get it, so leaving now loses
   * it for good. Never blocks a resumed session (no secret is on the page to
   * lose) or one already copied/downloaded.
   */
  const shouldBlockLeave = useCallback(
    () => !!mintedToken && !secretHandedOff,
    [mintedToken, secretHandedOff],
  );
  const leaveBlocker = useBlocker({
    shouldBlockFn: shouldBlockLeave,
    enableBeforeUnload: true,
    withResolver: true,
  });

  function selectOs(os: ProbeOs) {
    setProbeOs(os);
    setShowRawCommand(false);
    setShowRequirements(false);
    setShowOsSwitcher(false);
    setOsChooserOpen(false);
  }

  // SIL-1197: "On a successful build, focus moves to the 'Your probe is
  // ready' heading." mintedToken only ever transitions null -> a value (or
  // value -> a new value on a replace-probe build) - never runs while it's
  // still null, so this doesn't fire on mount before a probe exists.
  useEffect(() => {
    if (mintedToken) readyHeadingRef.current?.focus();
  }, [mintedToken]);

  const loadTokens = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.api.client.inventory.tokens.$get();
      if (res.ok) setTokens((await res.json()).tokens as InventoryTokenRow[]);
    } catch {
      notify('Could not load your probes.', 'error');
    } finally {
      setTokensLoaded(true);
    }
  }, [isAdmin, notify]);

  useEffect(() => {
    void loadTokens();
  }, [loadTokens]);

  // SIL-1194: progressive build-stage copy, scoped to a single `minting` run
  // - the timers reset (via the effect cleanup) the instant minting flips
  // off, whether that's success, failure, or a cancel.
  useEffect(() => {
    if (!minting) {
      setBuildStage('idle');
      return;
    }
    setBuildStage('building');
    const slowTimer = setTimeout(() => setBuildStage('slow'), stillBuildingMs);
    const stuckTimer = setTimeout(() => setBuildStage('stuck'), stuckMs);
    return () => {
      clearTimeout(slowTimer);
      clearTimeout(stuckTimer);
    };
  }, [minting, stillBuildingMs, stuckMs]);

  /** Bumped on every mint attempt and on cancel, so a mint whose response
   * arrives after the user cancelled (or started a retry) is silently
   * dropped instead of overwriting state a newer attempt owns. */
  const requestSeqRef = useRef(0);

  /** Entry point for every "generate"/"new token" button: decides whether a warning is needed first. */
  function requestMint() {
    if (minting) return;
    if (activeToken) {
      setConfirmMintOpen(true);
      return;
    }
    void mintToken();
  }

  async function mintToken() {
    setConfirmMintOpen(false);
    const seq = ++requestSeqRef.current;
    setMinting(true);
    try {
      const body = await withMinimumDuration(async () => {
        const res = await api.api.client.inventory.tokens.$post({ json: {} });
        if (res.status === 409) throw new MintConflictError();
        await throwIfNotOk(res);
        return (await res.json()) as { id: string; token: string };
      }, minBuildMs);
      if (seq !== requestSeqRef.current) return; // superseded by a cancel/retry - ignore
      setMintedTokenId(body.id);
      setMintedToken(body.token);
      setSecretHandedOff(false); // SIL-1208: a fresh secret needs its own hand-off
      setShowOsSwitcher(false); // SIL-1226: a fresh build starts with the changer collapsed
      await loadTokens();
      onProbeBuilt?.();
    } catch (err) {
      if (seq !== requestSeqRef.current) return; // superseded by a cancel/retry - ignore
      if (err instanceof MintConflictError) {
        notify('Another probe change for this organization is in progress - try again.', 'error');
      } else {
        notify(BUILD_FAILURE_COPY, 'error');
      }
    } finally {
      if (seq === requestSeqRef.current) setMinting(false);
    }
  }

  /** Gives up on the in-flight build: any response it eventually gets is
   * ignored (see the `seq` check in `mintToken`). The request itself is not
   * aborted - only the UI stops waiting on it - since nothing here does
   * anything destructive server-side to undo. */
  function cancelMint() {
    requestSeqRef.current += 1;
    setMinting(false);
  }

  function retryMint() {
    cancelMint();
    requestMint();
  }

  async function revokeToken(tokenId: string) {
    try {
      const res = await api.api.client.inventory.tokens[':tokenId'].$delete({ param: { tokenId } });
      await throwIfNotOk(res);
      notify('Probe revoked.', 'success');
      await loadTokens();
    } catch {
      notify('Could not revoke that probe.', 'error');
    }
  }

  // SIL-1226: a prominent, labelled segmented control - larger hit area and a
  // clear accent-color selected state, so the OS choice is noticed on first
  // view without competing with the primary "Build" button below it (that
  // button stays a solid filled CTA; this stays an outlined/tinted toggle).
  // Kept separate from the trust/scope prose (`trustDisclosure`, below) so it
  // reads as a plain "pick your target" control, not buried inside a
  // paragraph about what the probe can do.
  const osButtons = (
    <div className="flex flex-wrap gap-2">
      {(Object.keys(PROBE_OS_LABELS) as ProbeOs[]).map((os) => {
        const Icon = PROBE_OS_ICONS[os];
        const selected = probeOs === os;
        return (
          <button
            key={os}
            type="button"
            onClick={() => selectOs(os)}
            aria-pressed={selected}
            className={cn(
              'flex min-w-[7rem] items-center justify-center gap-2 rounded-md border-2 px-4 py-2.5 text-sm font-medium transition-colors',
              selected
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:bg-muted/50',
            )}
          >
            <Icon className="h-4 w-4" />
            {PROBE_OS_LABELS[os]}
          </button>
        );
      })}
    </div>
  );
  // SIL-1812: a detected linux/windows host gets one line, "Built for Windows
  // - Change"; macOS / unknown keep both buttons under a plain question (no
  // "Auto-detected" text, and no second "different machine" line).
  const osDetected = detectedOs === 'linux' || detectedOs === 'windows';
  const osTabs =
    osDetected && !osChooserOpen ? (
      <p className="text-xs text-foreground">
        Built for {PROBE_OS_LABELS[probeOs]} -{' '}
        <button
          type="button"
          onClick={() => setOsChooserOpen(true)}
          className="font-medium text-muted-foreground underline hover:text-foreground"
        >
          Change
        </button>
      </p>
    ) : (
      <div className="space-y-1.5">
        <p className="text-xs font-semibold text-foreground">Which machine will you add?</p>
        {osButtons}
      </div>
    );

  // SIL-1213: the trust/scope paragraphs - collapsed by default (AC: "The
  // trust/scope paragraphs move into a collapsed 'What this probe can and
  // can't do' disclosure"). Previously rendered inline, ahead of any
  // instruction, as part of the ~200 words the root cause calls out.
  const trustDisclosure = (
    <DisclosurePanel title="What this probe can and can't do" icon={ShieldCheck}>
      <div className="space-y-2 text-xs">
        <p>One probe works on all your machines for 90 days. It is built for your account only.</p>
        <p>
          The probe can only add or update hardware details in your account. It cannot see your other
          assets or prices, and it cannot list, sell or transfer anything.
        </p>
        <div>
          <p className="font-medium text-foreground">What it will never do on your machine</p>
          <ul className="list-disc space-y-1 pl-4">
            <li>Does not change system settings</li>
            <li>Does not run in the background</li>
            <li>Does not access other assets or pricing data</li>
          </ul>
        </div>
      </div>
    </DisclosurePanel>
  );

  // SIL-1213: the full requirements box - now collapsed by default (AC: "The
  // requirements are collapsed by default and can be expanded"), both
  // pre-build and post-build. "Paste the command below..." no longer lives
  // here - see `RUN_INSTRUCTIONS`, rendered only next to the built command.
  const requirementsDetail = (
    <ul className="list-disc space-y-1 pl-4 text-xs">
      {PROBE_REQUIREMENTS[probeOs].requirements.map((req) => (
        <li key={req}>{req}</li>
      ))}
    </ul>
  );

  // SIL-1213: pre-build requirements disclosure - collapsed by default.
  const requirementsPreBuild = (
    <DisclosurePanel title={`${PROBE_OS_LABELS[probeOs]} requirements`} icon={ClipboardList}>
      {requirementsDetail}
    </DisclosurePanel>
  );

  // SIL-1197: post-build, the requirements list collapses to this one-line
  // summary ("Linux requirements ✓") with a working disclosure - the full
  // list (`requirementsDetail`) only renders while `showRequirements` is true.
  const requirementsSummary = (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/20 px-3 py-2 text-xs">
        <span className="font-medium text-foreground">{PROBE_OS_LABELS[probeOs]} requirements ✓</span>
        <button
          type="button"
          onClick={() => setShowRequirements((v) => !v)}
          aria-expanded={showRequirements}
          className="flex shrink-0 items-center gap-1 font-medium text-muted-foreground hover:text-foreground"
        >
          {showRequirements ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          Show requirements
        </button>
      </div>
      {showRequirements && (
        <div className="rounded-md border border-border bg-muted/20 p-3">{requirementsDetail}</div>
      )}
    </div>
  );

  // SIL-1194/SIL-1812: the pre-build build action, now FIRST in the panel:
  // one sentence + the button (no title, no dashed box, no lock icon), with
  // the spinner/progressive-copy/cancel-retry states layered onto the button
  // while `minting` is true.
  const buildAction =
    isAdmin && !tokensLoaded ? (
      // Until the probe list is in, "Build" could skip the replace warning
      // (activeToken would read null), so it is not offered yet.
      <LoadingShell rows={2} />
    ) : isAdmin ? (
      <div className="space-y-3">
        <p className="text-sm text-foreground">
          We build a probe for your account. It reads your hardware and sends verified specs to your
          portfolio - no typing.
        </p>
        <Button onClick={requestMint} disabled={minting} aria-busy={minting} className="gap-2">
          {minting ? (
            <span
              aria-hidden="true"
              className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
            />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          {minting ? (buildStage === 'building' ? 'Building your probe…' : 'Still building…') : 'Build my TrueRead probe'}
        </Button>
        {minting && buildStage === 'stuck' && (
          <div className="flex gap-3 text-xs">
            <button
              type="button"
              onClick={cancelMint}
              className="font-medium text-muted-foreground underline hover:text-foreground"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={retryMint}
              className="font-medium text-muted-foreground underline hover:text-foreground"
            >
              Retry
            </button>
          </div>
        )}
        {/* Polite live announcement (SIL-1194) - visually redundant with the
            button label above, sr-only so it isn't duplicated on screen. */}
        <p role="status" aria-live="polite" className="sr-only">
          {minting
            ? buildStage === 'stuck'
              ? 'Still building your probe. You can cancel or retry.'
              : buildStage === 'slow'
                ? 'Still building your probe.'
                : 'Building your probe.'
            : ''}
        </p>
      </div>
    ) : (
      <p className="text-sm text-foreground">Ask an org admin to build a TrueRead probe.</p>
    );

  // SIL-1197: identical in the Windows and Linux/default post-build branches
  // below - one place, not two copies drifting. `readyHeadingRef` is where
  // focus lands right after a successful build.
  const readyHeading = (
    <div className="rounded-md border border-border bg-muted/20 p-3">
      <h3 ref={readyHeadingRef} tabIndex={-1} className="text-sm font-medium text-foreground focus:outline-none">
        Your probe is ready
      </h3>
      <p className="mt-1 text-xs">
        This probe is tied to your account and won&rsquo;t run under any other. Run it on each machine
        you&rsquo;re capturing - the same probe works on all of them.
      </p>
      {/* SIL-1226: swaps the pasted command between Linux/Windows forms only -
          no new mint, no revoke, and the readings poll below (keyed on
          `mintedTokenId`, not `probeOs`) is untouched by this. */}
      <button
        type="button"
        onClick={() => setShowOsSwitcher((v) => !v)}
        aria-expanded={showOsSwitcher}
        className="mt-2 text-xs font-medium text-muted-foreground underline hover:text-foreground"
      >
        Wrong OS? Change it
      </button>
      {showOsSwitcher && (
        <div className="mt-2 space-y-1.5">
          <p className="text-xs font-semibold text-foreground">Which machine will you add?</p>
          {osButtons}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Merged with probe issuance (was two cards): the command cannot be
          copied until a probe exists. One flow: build (admin) -> full
          one-liner, probe baked in -> copy -> paste. */}
      <Card>
        {hasSession && <PanelTitle title="Your TrueRead probe" />}
        <div className={cn('p-4 text-sm text-muted-foreground', hasSession && 'pt-0')}>
          {/* SIL-1213: single-column, sequential reading order throughout -
              the build action is reached last (root-cause fix: it used to
              sit in a parallel 2fr/1fr panel, reachable before any of the
              instructions above it had been read). "How this works" is
              always first. */}
          <div className="grid grid-cols-1 gap-3">
            {!hasSession ? (
              <>
                {buildAction}
                {detectedOs === 'macos' && <MacNotSupportedNotice />}
                {osTabs}
                <HowThisWorksStrip current={howStep} />
                {trustDisclosure}
                {requirementsPreBuild}
              </>
            ) : !mintedToken ? (
              // SIL-1208: resumed from a `resumeTokenId` (e.g. a reload) - the
              // token id survived, but the secret itself never does. States
              // that plainly rather than pretending nothing was built, and
              // keeps the readings feed live either way.
              <div className="space-y-3">
                {!hideBuildContextOnceMinted && (
                  <>
                    <HowThisWorksStrip current={howStep} />
                    {osTabs}
                    {trustDisclosure}
                    {requirementsSummary}
                  </>
                )}
                <div
                  className={cn(
                    'rounded-md border p-3',
                    resumedTokenRevoked ? 'border-destructive/50 bg-destructive/10' : 'border-border bg-muted/20',
                  )}
                >
                  <h3 className="text-sm font-medium text-foreground">
                    {resumedTokenRevoked ? 'This probe is no longer active' : 'Resuming your probe session'}
                  </h3>
                  <p className="mt-1 text-xs">
                    {resumedTokenRevoked
                      ? "This probe was replaced by a newer one (or revoked) - any command copied from it has already stopped working. Build a new probe to keep capturing."
                      : "This probe's command was shown once, right after it was built, and can't be shown again - reloading or leaving the page doesn't bring it back. Commands already pasted on target machines keep working; watch readings below, or build a new probe."}
                  </p>
                  {isAdmin && (
                    <Button
                      variant={resumedTokenRevoked ? 'primary' : 'secondary'}
                      size="sm"
                      onClick={requestMint}
                      disabled={minting}
                      className="mt-2 gap-1"
                    >
                      <Plus className="h-4 w-4" /> {minting ? 'Building your probe…' : 'Build new probe'}
                    </Button>
                  )}
                </div>
                {mintedTokenId && (
                  <ProbeIngestFeed tokenId={mintedTokenId} onMatchesChange={onMatchesChange} onRowsChange={handleRowsChange} />
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {!hideBuildContextOnceMinted && (
                  <>
                    <HowThisWorksStrip current={howStep} />
                    {osTabs}
                    {trustDisclosure}
                    {requirementsSummary}
                  </>
                )}
                {command && probeOs === 'windows' && launcher ? (
                  <>
                    {/* SIL-1127: one-click .cmd is the primary path on Windows now - the
                        raw copy-paste command that used to be the only option moves
                        behind "Need more control?" below. */}
                    {readyHeading}
                    <p className="text-xs">This file is specific to your account. Keep it to yourself.</p>
                    <Button
                      onClick={() => {
                        downloadLauncher(launcher, accountSlug);
                        setSecretHandedOff(true);
                      }}
                      className="gap-1"
                    >
                      <Download className="h-4 w-4" /> Download launcher (.cmd)
                    </Button>
                    <p className="text-xs">
                      Downloads a small <code>.cmd</code> file with your probe baked in. Double-click it on the target
                      Windows machine to run the probe - no PowerShell paste needed.
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowRawCommand((v) => !v)}
                      className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                    >
                      {showRawCommand ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                      Need more control?
                    </button>
                    {showRawCommand && (
                      <>
                        {/* SIL-1213: "Paste the command below..." only ever renders once a
                            command actually exists to paste - right next to it. */}
                        <p className="text-xs">{RUN_INSTRUCTIONS.windows}</p>
                        <CommandBlock
                          command={command}
                          maskedCommand={maskedCommand ?? ''}
                          notify={notify}
                          onAcquired={() => setSecretHandedOff(true)}
                        />
                      </>
                    )}
                    <p>
                      The checksum above is checked before anything runs, and the command stops if it does not match. The
                      last step runs the probe in a child PowerShell with script execution enabled for that process only -
                      Windows blocks all scripts by default, and nothing on the machine is changed. The probe needs no
                      Administrator rights, and it refuses to run inside a virtual machine - under WSL, run it on the
                      Windows host rather than the Linux probe inside it.
                    </p>
                    {/* SIL-1213: Copy -> Run on the target machine -> Readings, joined
                        visually into one panel so the reading feed reads as the direct
                        continuation of the command above it, not a separate block. */}
                    {mintedTokenId && (
                      <div className="rounded-md border border-border p-3">
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Readings from the target machine
                        </p>
                        <ProbeIngestFeed
                          tokenId={mintedTokenId}
                          onMatchesChange={onMatchesChange}
                          onRowsChange={handleRowsChange}
                        />
                      </div>
                    )}
                  </>
                ) : command ? (
                  <>
                    {readyHeading}
                    {/* SIL-1213: Copy -> Run on the target machine -> Readings appear
                        below, in reading order and visually joined into one panel. */}
                    <div className="rounded-md border border-border">
                      <div className="space-y-3 p-3">
                        <p className="text-xs">
                          {/* SIL-1208: one consistent rule, stated once - visible only in this
                              browser session, never recoverable after a reload. Not "shown
                              only once": Reveal can show it again within the same session,
                              it just can't survive leaving the page. */}
                          Copy the whole command now - it&rsquo;s visible only in this browser session. If you reload
                          or leave this page, it can&rsquo;t be shown again.
                        </p>
                        {/* SIL-1213: "Paste the command below..." only ever renders once a
                            command actually exists to paste - right next to it. */}
                        <p className="text-xs">{RUN_INSTRUCTIONS.linux}</p>
                        <CommandBlock
                          command={command}
                          maskedCommand={maskedCommand ?? ''}
                          notify={notify}
                          onAcquired={() => setSecretHandedOff(true)}
                        />
                        <p className="text-xs">
                          The checksum above is checked before anything runs, and the command stops if it does not
                          match. The Linux probe needs no root, and it refuses to run inside a virtual machine.
                        </p>
                      </div>
                      {mintedTokenId && (
                        <div className="border-t border-border p-3">
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Readings from the target machine
                          </p>
                          <ProbeIngestFeed
                            tokenId={mintedTokenId}
                            onMatchesChange={onMatchesChange}
                            onRowsChange={handleRowsChange}
                          />
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-6 text-center">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                    <p className="text-foreground">
                      Something is wrong with the probe this page just received - it does not look like a real probe. Do
                      not run a command with it.
                    </p>
                    <p className="text-xs">Reload the page and build a new one; contact support if this repeats.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {isAdmin && showTokenManagement && (
          <div className="space-y-3 border-t border-border p-4">
            <div className="flex items-center justify-between gap-2">
              <PanelTitle title="Your probes" />
              <Button variant="secondary" size="sm" onClick={requestMint} disabled={minting} className="gap-1">
                <Plus className="h-4 w-4" /> {minting ? 'Building your probe…' : 'Build new probe'}
              </Button>
            </div>

            {tokens.length > 0 && (
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="py-1">Created</th>
                    <th className="py-1">Expires</th>
                    <th className="py-1">Last used</th>
                    <th className="py-1" />
                  </tr>
                </thead>
                <tbody>
                  {tokens.map((t) => (
                    <tr key={t.id} className="border-t border-border">
                      <td className="py-2">{new Date(t.createdAt).toLocaleDateString()}</td>
                      <td className="py-2">{new Date(t.expiresAt).toLocaleDateString()}</td>
                      <td className="py-2">
                        {t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleDateString() : 'never'}
                      </td>
                      <td className="py-2 text-right">
                        {t.revokedAt ? (
                          <span className="text-xs text-muted-foreground">revoked</span>
                        ) : (
                          <Button variant="secondary" size="sm" onClick={() => void revokeToken(t.id)} className="gap-1">
                            <Trash2 className="h-3 w-3" /> Revoke
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </Card>

      <MintConfirmDialog
        open={confirmMintOpen}
        activeToken={activeToken}
        busy={minting}
        onCancel={() => setConfirmMintOpen(false)}
        onConfirm={() => void mintToken()}
      />

      <LeaveGuardDialog
        open={leaveBlocker.status === 'blocked'}
        onStay={() => leaveBlocker.reset?.()}
        onLeave={() => leaveBlocker.proceed?.()}
      />
    </>
  );
}
